import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { LoadingSpinner, StatusBadge } from '../../components/UI';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://cmsapi3.vdavns.com/api').replace('/api', '');

export default function DownloadNOC() {
  const [apps, setApps]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/applications/my')
      .then(r => setApps((r.applications || []).filter(a => a.status === 'approved_final')))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (app) => {
    if (!app.final_noc_pdf) {
      return toast.error('NOC PDF not uploaded yet. Please contact the department.');
    }

    const fileUrl = `${API_BASE}${app.final_noc_pdf}`;
    const fileName = `NOC-${app.application_no}-${app.noc_name.replace(/\s+/g, '-')}.pdf`;

    try {
      toast.loading('Preparing download...', { id: 'dl' });

      // Method 1: Try fetch as blob
      const token = localStorage.getItem('noc_token');
      const response = await fetch(fileUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error(`${response.status}`);

      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Empty file');

      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: 'application/pdf' })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.dismiss('dl');
      toast.success('NOC downloaded successfully!');

    } catch (err) {
      toast.dismiss('dl');
      // Method 2: Fallback — open in new tab (always works)
      console.log('Blob fetch failed, using direct link:', err.message);
      window.open(fileUrl, '_blank');
      toast.success('Opening NOC in new tab. Use Ctrl+S to save.');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Download Approved NOC</h1>
      <p className="text-sm text-gray-500 mb-6">Download your approved NOC certificates</p>

      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : apps.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-5xl mb-3">📭</p>
          <p className="text-gray-600 font-medium">No approved NOCs found</p>
          <p className="text-sm text-gray-400 mt-1">
            Approved NOCs will appear here once issued by the department
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {apps.map(app => (
            <div key={app.id} className="card flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">
                  🏆
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-bold text-gray-800">{app.noc_name}</h3>
                    <StatusBadge status={app.status} />
                  </div>
                  <p className="text-sm text-gray-500 font-mono">{app.application_no}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Approved: {app.approved_at
                      ? format(new Date(app.approved_at), 'dd MMM yyyy')
                      : 'N/A'}
                  </p>
                  <p className="text-xs mt-1">
                    {app.final_noc_pdf
                      ? <span className="text-green-600">✅ PDF Ready</span>
                      : <span className="text-yellow-600">⏳ Being prepared by department</span>}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 flex-shrink-0">
                {app.final_noc_pdf ? (
                  <button
                    onClick={() => handleDownload(app)}
                    className="btn-success flex items-center gap-2 px-5 py-2.5">
                    ⬇️ Download NOC PDF
                  </button>
                ) : (
                  <button disabled
                    className="bg-gray-100 text-gray-400 font-semibold px-4 py-2 rounded-lg cursor-not-allowed text-sm">
                    ⏳ Not Ready
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}